// AbsencesPage.jsx — CONTAINER (dono da lógica) da tela /absences. Monta { vm, actions } reais e
// renderiza a AbsencesView (View pura do Claude Design). Formato View/ViewModel (docs/DESIGN-FLOW.md).
// Substitui o antigo ScheduleBlocksPage (mantido como .legacy para referência).
import { useState, useMemo, useCallback } from 'react';

import { useScheduleBlocks } from '../hooks/queries';
import useDialog from '../hooks/useDialog';
import useSnackbar from '../hooks/useSnackbar';
import { useContextualHint } from '../hooks/useContextualHint';
import { formatDate, getDurationInDays } from '../utils/formatters';
import { pluralizeWithCount, pluralizePastParticiple } from '../utils/pluralize';

import ScheduleBlockForm from '../components/forms/ScheduleBlockForm';
import CascadeDeleteDialog from '../components/ui/CascadeDeleteDialog';
import AppSnackbar from '../components/ui/AppSnackbar';
import AbsencesView from './absences/AbsencesView';

const COLUMN_LABELS = {
  professional: 'Profissional', place: 'Local', start: 'Início', end: 'Fim',
  duration: 'Duração', reason: 'Motivo', status: 'Status', actions: 'Ações',
};
const PAGE_SIZE_OPTIONS = [5, 10, 25, 50];
const STATUS_META = {
  scheduled: { label: 'Agendado', tone: 'info' },
  active: { label: 'Em vigor', tone: 'success' },
  expired: { label: 'Encerrado', tone: 'neutral' },
};

// status derivado das datas (a View não calcula — recebe pronto)
function getBlockStatus(startDate, endDate) {
  const now = Date.now();
  if (now < new Date(startDate).getTime()) return 'scheduled';
  if (now > new Date(endDate).getTime()) return 'expired';
  return 'active';
}

export default function AbsencesPage() {
  const hint = useContextualHint('absences', {});
  const [page, setPage] = useState(0); // 0-based interno; a View usa 1-based
  const [pageSize, setPageSize] = useState(10);
  const [filterProfessional, setFilterProfessional] = useState('');
  const [filterPlace, setFilterPlace] = useState('');

  const filters = useMemo(() => {
    const f = {};
    if (filterProfessional) f.professionalId = filterProfessional;
    if (filterPlace) f.placeId = filterPlace;
    return f;
  }, [filterProfessional, filterPlace]);

  const {
    data, scheduleBlocks, professionals, places, totalCount,
    loading, error, reload, createAsync, updateAsync,
  } = useScheduleBlocks({ page: page + 1, pageSize, filters });

  const formDialog = useDialog();
  const deleteDialog = useDialog();
  const { snackbar, showSuccess, showError, hideSnackbar } = useSnackbar();

  const professionalsMap = useMemo(
    () => new Map((data?.professionals || []).map((p) => [p.id, p])),
    [data?.professionals]
  );
  const placesMap = useMemo(
    () => new Map((data?.places || []).map((p) => [p.id, p])),
    [data?.places]
  );

  // ── ViewModel ────────────────────────────────────────────────────────────────
  const isFiltered = !!(filterProfessional || filterPlace);
  const rows = useMemo(() => (scheduleBlocks || []).map((b) => {
    const prof = professionalsMap.get(b.professionalId);
    const place = placesMap.get(b.placeId);
    const status = getBlockStatus(b.startDate, b.endDate);
    const meta = STATUS_META[status];
    return {
      id: b.id,
      professionalName: prof?.name || 'N/A',
      professionalSpecialty: prof?.specialty || null,
      placeName: place?.name || 'N/A',
      startLabel: formatDate(b.startDate),
      endLabel: formatDate(b.endDate),
      durationLabel: getDurationInDays(b.startDate, b.endDate),
      reason: b.reason || null,
      status,
      statusLabel: meta.label,
      statusTone: meta.tone,
    };
  }), [scheduleBlocks, professionalsMap, placesMap]);

  const rowsEmpty = rows.length === 0;
  const viewState = (loading && !data) ? 'loading'
    : (error && !data) ? 'error'
    : (rowsEmpty && !isFiltered) ? 'empty'
    : 'ready';

  const from = totalCount === 0 ? 0 : page * pageSize + 1;
  const to = Math.min((page + 1) * pageSize, totalCount);

  const vm = {
    viewState,
    errorMessage: error ? 'Não foi possível carregar as ausências. Tente novamente.' : null,
    title: 'Ausências',
    subtitle: 'Gerencie períodos em que profissionais não estarão disponíveis para atendimento',
    actionLabel: 'Nova Ausência',
    hint: hint?.message || '',
    filters: {
      professionalId: filterProfessional,
      placeId: filterPlace,
      professionals: (professionals || []).map((p) => ({
        id: p.id, label: p.specialty ? `${p.name} — ${p.specialty}` : p.name,
      })),
      places: (places || []).map((p) => ({ id: p.id, label: p.name })),
      canClear: isFiltered,
    },
    rows,
    pagination: {
      page: page + 1,
      pageCount: Math.max(1, Math.ceil(totalCount / pageSize)),
      pageSize,
      total: totalCount,
      pageSizeOptions: PAGE_SIZE_OPTIONS,
      rangeLabel: `${from}–${to} de ${totalCount}`,
    },
    isFilteredEmpty: rowsEmpty && isFiltered,
    emptyFilteredMessage: 'Nenhuma ausência encontrada com os filtros selecionados',
    columnLabels: COLUMN_LABELS,
  };

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const byId = useCallback((id) => (scheduleBlocks || []).find((b) => b.id === id), [scheduleBlocks]);

  const handleCreate = async (formData) => {
    try {
      await Promise.all(formData.placeProfessionalIds.map((ppId) => createAsync({
        placeProfessionalId: ppId, startDate: formData.startDate, endDate: formData.endDate, reason: formData.reason,
      })));
      formDialog.handleClose();
      const n = formData.placeProfessionalIds.length;
      showSuccess(`${pluralizeWithCount(n, 'ausência', 'ausências')} ${pluralizePastParticiple(n, 'cadastrado', 'cadastrada', 'f')} com sucesso!`);
    } catch (err) {
      showError('Erro ao cadastrar ausência: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleUpdate = async (formData) => {
    try {
      await updateAsync(formDialog.data.id, {
        placeProfessionalId: formData.placeProfessionalIds[0],
        startDate: formData.startDate, endDate: formData.endDate, reason: formData.reason,
      });
      formDialog.handleClose();
      showSuccess('Ausência atualizada com sucesso!');
    } catch (err) {
      showError('Erro ao atualizar ausência: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleSubmit = formDialog.isEdit ? handleUpdate : handleCreate;

  const actions = {
    onNew: () => formDialog.handleOpen(null),
    onEdit: (id) => formDialog.handleOpen(byId(id)),
    onDelete: (id) => deleteDialog.handleOpen(byId(id)),
    onFilterProfessional: (id) => { setFilterProfessional(id); setPage(0); },
    onFilterPlace: (id) => { setFilterPlace(id); setPage(0); },
    onClearFilters: () => { setFilterProfessional(''); setFilterPlace(''); setPage(0); },
    onChangePage: (p) => setPage(Math.max(0, p - 1)),
    onChangePageSize: (size) => { setPageSize(size); setPage(0); },
    onRetry: reload,
  };

  return (
    <>
      <AbsencesView vm={vm} actions={actions} />

      <ScheduleBlockForm
        open={formDialog.open}
        onClose={formDialog.handleClose}
        onSubmit={handleSubmit}
        initialData={formDialog.data}
        isEdit={formDialog.isEdit}
        professionals={data?.professionals || []}
        places={data?.places || []}
        placeProfessionals={data?.placeProfessionals || []}
      />

      <CascadeDeleteDialog
        open={deleteDialog.open}
        onClose={deleteDialog.handleClose}
        onDeleted={() => { deleteDialog.handleClose(); showSuccess('Excluído com sucesso!'); reload(); }}
        deletePath={deleteDialog.data ? `/blocks/${deleteDialog.data.id}` : ''}
        entityName={deleteDialog.data?.reason}
      />

      <AppSnackbar snackbar={snackbar} onClose={hideSnackbar} />
    </>
  );
}
